/// <amd-module name="Wit/Common/Controls/Boards/InsertionMarker" />
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { css } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { Portal } from "VSSUI/Portal";

export interface IInsertionMarkerPosition {
    top: number;
    left: number;
    width: number;
}

export interface IInsertionMarkerProps {
    insertionMarkerPosition: IObservableValue<IInsertionMarkerPosition | undefined>;
}

export const InsertionMarker: React.FunctionComponent<IInsertionMarkerProps> = ({ insertionMarkerPosition }) => (
    <Observer position={insertionMarkerPosition}>
        {({ position }: { position: IInsertionMarkerPosition | undefined }) =>
            position ? (
                <Portal>
                    <div
                        className={css("board-drop-indicator-circle flex-grow absolute")}
                        style={{
                            position: "absolute",
                            top: position.top - 3,
                            left: position.left
                        }}
                    />
                    <div
                        className="board-drop-indicator-line absolute"
                        style={{
                            top: position.top,
                            left: position.left,
                            width: position.width
                        }}
                    />
                </Portal>
            ) : null
        }
    </Observer>
);

function getCandidates(parentNode: ParentNode, selector: string, filter?: (item: HTMLElement) => boolean): HTMLElement[] {
    const candidates = Array.from(parentNode.querySelectorAll<HTMLElement>(selector));
    return filter ? candidates.filter(filter) : candidates;
}

function compute1DDistance(beginningPoint: number, endPoint: number, searchingPoint: number): number {
    if (searchingPoint >= beginningPoint && searchingPoint <= endPoint) {
        return 0;
    }

    const distanceFromBeginning = Math.abs(beginningPoint - searchingPoint);
    const distanceFromEnd = Math.abs(endPoint - searchingPoint);
    const distance = distanceFromBeginning > distanceFromEnd ? distanceFromEnd : distanceFromBeginning;

    return distance;
}

type Direction = "horizontal" | "vertical";

function computeCloserElement(firstElement: HTMLElement, secondElement: HTMLElement, searchingPoint: number, direction: Direction): HTMLElement {
    const start = direction === "horizontal" ? "left" : "top";
    const end = direction === "horizontal" ? "right" : "bottom";

    const firstElementRect = firstElement.getBoundingClientRect();
    const secondElementRect = secondElement.getBoundingClientRect();

    const firstElementDistance = compute1DDistance(firstElementRect[start], firstElementRect[end], searchingPoint);
    const secondElementDistance = compute1DDistance(secondElementRect[start], secondElementRect[end], searchingPoint);

    return firstElementDistance < secondElementDistance ? firstElement : secondElement;
}

const findClosestElement = (
    parentNode: ParentNode,
    selector: string,
    searchingPoint: number,
    direction: Direction,
    filter?: (item: HTMLElement) => boolean
) =>
    getCandidates(parentNode, selector, filter).reduce(
        (currentClosestItem, candidate) =>
            !currentClosestItem ? candidate : computeCloserElement(currentClosestItem, candidate, searchingPoint, direction),
        undefined as HTMLElement | undefined
    );

export const findElementClosestToVerticalPoint = (
    parentNode: ParentNode,
    selector: string,
    pageY: number,
    filter?: (item: HTMLElement) => boolean
): HTMLElement | undefined => findClosestElement(parentNode, selector, pageY, "vertical", filter);

export const findElementClosestToHorizontalPoint = (
    parentNode: ParentNode,
    selector: string,
    pageX: number,
    filter?: (item: HTMLElement) => boolean
): HTMLElement | undefined => findClosestElement(parentNode, selector, pageX, "horizontal", filter);
